import { useEffect, useRef, useState } from 'react';
import { Box, Card, Icon, Link } from '@nimbus-ds/components';
import { PlusCircleIcon } from '@nimbus-ds/icons';
import { useResponsive } from 'commons/components';
import { useBoolean } from 'commons/hooks';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import {
  SELECTED_PRODUCTS_LIMIT,
  TransferProductVariant,
} from 'domains/Catalog/Transfers/utils';
import {
  ProductInterface,
  ProductSearcherConfirmTransferCard,
} from 'domains/Orders/components/ProductSearcher';
import {
  ProductSearcher,
  ProductSearcherFooterProps,
} from 'domains/Orders/components/ProductSearcher/ProductSearcher';
import { useLocations } from 'domains/Shipping/Locations/hooks';
import TransferList from '../TransferList';
import TransferTable from '../TransferTable';

interface ProductsTransferCardProps {
  productVariants: TransferProductVariant[];
  sourceLocationId: string;
  showZeroQuantityAsWarning: boolean;
  onAddProducts: (products: TransferProductVariant[]) => void;
  onTransferQuantityChange: (
    productId: string,
    variantId: string,
    quantity: number,
  ) => void;
  onApplyValuesToAllVariants: (variantId: string) => void;
  onRemoveProduct: (productId: string, variantId: string) => void;
  onTransferTotalStock: (productId: string) => void;
}

function ProductsTransferCard({
  productVariants,
  sourceLocationId,
  showZeroQuantityAsWarning,
  onAddProducts,
  onTransferQuantityChange,
  onApplyValuesToAllVariants,
  onRemoveProduct,
  onTransferTotalStock,
}: Readonly<ProductsTransferCardProps>) {
  const t = useTranslationCatalog();
  const { isMobile } = useResponsive();

  const footerRef = useRef<HTMLDivElement>(null);

  const [isSelectedProductsCount, setIsSelectedProductsCount] =
    useState<boolean>(false);

  const [footerHeight, setFooterHeight] = useState<string>('70px');

  const [isProductSearcherOpen, openProductSearcher, closeProductSearcher] =
    useBoolean(false);

  const { locations } = useLocations();

  const sourceLocation = locations.find(
    (location) => location.id === sourceLocationId,
  );

  const location = { id: sourceLocationId, name: sourceLocation?.name ?? '' };

  const handleProductSearcherSubmit = (
    newProducts: ProductInterface<false>[],
  ) => {
    const newTransferProductsToAdd = newProducts.map((product) => ({
      ...product,
      quantity: undefined,
    }));
    onAddProducts(newTransferProductsToAdd);
    closeProductSearcher();
  };

  const onRenderFooter = (footerProps: ProductSearcherFooterProps<false>) => {
    setIsSelectedProductsCount(footerProps.selectedLimitReached);
    return (
      <div ref={footerRef} style={{ width: '100%' }}>
        <ProductSearcherConfirmTransferCard footerProps={footerProps} />
      </div>
    );
  };

  useEffect(() => {
    if (footerRef.current) {
      setFooterHeight(`${footerRef.current.clientHeight + 24}px`);
    }
  }, [isSelectedProductsCount]);

  return (
    <>
      <Card>
        <Card.Header title={t('transfers.new.products.title')} />
        <Card.Body>
          <Box display="flex" flexDirection="column" gap="4">
            <Link
              as="button"
              appearance="primary"
              onClick={openProductSearcher}
              textDecoration="none"
            >
              <Icon
                color="primary-interactive"
                source={<PlusCircleIcon size={14} />}
              />
              {t('transfers.new.products.select')}
            </Link>

            {isMobile ? (
              <TransferList
                productVariants={productVariants}
                showZeroQuantityAsWarning={showZeroQuantityAsWarning}
                onTransferQuantityChange={onTransferQuantityChange}
                onRemoveProduct={onRemoveProduct}
                onApplyValuesToAllVariants={onApplyValuesToAllVariants}
                onTransferTotalStock={onTransferTotalStock}
              />
            ) : (
              <TransferTable
                productVariants={productVariants}
                showZeroQuantityAsWarning={showZeroQuantityAsWarning}
                onTransferQuantityChange={onTransferQuantityChange}
                onRemoveProduct={onRemoveProduct}
                onTransferTotalStock={onTransferTotalStock}
                onApplyValuesToAllVariants={onApplyValuesToAllVariants}
              />
            )}
          </Box>
        </Card.Body>
      </Card>
      <ProductSearcher<false>
        onClose={closeProductSearcher}
        onSubmit={handleProductSearcherSubmit}
        isOpen={isProductSearcherOpen}
        preselectedProducts={productVariants}
        location={location}
        renderFooterCallback={onRenderFooter}
        selectedProductsLimit={SELECTED_PRODUCTS_LIMIT}
        showPrice={false}
        allowInfiniteStockSelection={false}
        footerHeight={footerHeight}
      />
    </>
  );
}

export default ProductsTransferCard;
